import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { dashboardAPI } from '../../api/dashboard';
import './UniversalDashboard.css';

// Default stats when API is not available
const DEFAULT_STATS = {
   totalProjects: 0,
   totalSales: 0,
   totalOrders: 0,
   activeWebsites: 0,
   ended: 0,
   running: 0,
   pending: 0
};

const UniversalDashboard = () => {
   const navigate = useNavigate();
   const [user, setUser] = useState(null);
   const [generatedWebsites, setGeneratedWebsites] = useState([]);
   const [dashboardStats, setDashboardStats] = useState(DEFAULT_STATS);
   const [isLoading, setIsLoading] = useState(true);
   const [activeTab, setActiveTab] = useState('dashboard');
   const [analyticsData, setAnalyticsData] = useState(null);

   useEffect(() => {
      const token = Cookies.get('jwt');
      if (!token) {
         navigate('/');
         return;
      }
      try {
         const decoded = jwtDecode(token);
         setUser({
            name: decoded.email.split('@')[0],
            email: decoded.email,
            userType: decoded.userType || 'business' // Default to business for demo if missing
         });

         // Fetch real dashboard data
         fetchDashboardData();

      } catch (e) {
         console.error(e);
         navigate('/');
      }
   }, [navigate]);

   const fetchDashboardData = async () => {
      try {
         // Fetch dashboard statistics
         const statsData = await dashboardAPI.getDashboardStats();
         setDashboardStats(statsData || DEFAULT_STATS);

         // Fetch user websites
         const websitesData = await dashboardAPI.getUserWebsites();
         setGeneratedWebsites(websitesData?.websites || []);

         // Fetch analytics data
         const analyticsData = await dashboardAPI.getAnalytics();
         setAnalyticsData(analyticsData);

         setIsLoading(false);
      } catch (error) {
         console.error('Error fetching dashboard data:', error);
         // Set default values if API fails
         setDashboardStats(DEFAULT_STATS);
         setGeneratedWebsites([]);
         setIsLoading(false);
      }
   };

   // Refresh dashboard data
   const refreshData = () => {
      setIsLoading(true);
      fetchDashboardData();
   };

   const handleLogout = () => {
      Cookies.remove('jwt');
      navigate('/');
   };

   if (isLoading) return <div className="loader"></div>;

   // --- CUSTOMER VIEW ---
   if (user?.userType === 'customer') {
      return (
         <div className="customer-dashboard">
            <div className="customer-welcome">
               <h1>Hello, {user.name}!</h1>
               <p>Ready to explore amazing businesses?</p>
            </div>

            <div className="container" style={{ marginTop: '-30px' }}>
               {/* WALLET / POINTS CARD */}
               <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: 30 }}>
                  <div className="stat-card" style={{ padding: '20px', borderRadius: '16px' }}>
                     <div className="stat-header"><span style={{ color: '#3b82f6', fontWeight: 600 }}>Wallet Balance</span> <ion-icon name="wallet-outline" style={{ color: '#3b82f6' }}></ion-icon></div>
                     <div className="stat-value" style={{ color: '#1e3a8a' }}>₹1,250</div>
                     <div className="stat-trend">Available to spend</div>
                  </div>
                  <div className="stat-card" style={{ padding: '20px', borderRadius: '16px' }}>
                     <div className="stat-header"><span style={{ color: '#f59e0b', fontWeight: 600 }}>Loyalty Points</span> <ion-icon name="star-outline" style={{ color: '#f59e0b' }}></ion-icon></div>
                     <div className="stat-value" style={{ color: '#78350f' }}>450 pts</div>
                     <div className="stat-trend">Gold Tier</div>
                  </div>
                  <div className="stat-card highlight" onClick={() => navigate('/scan')} style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                     <div className="stat-header"><span style={{ color: 'white' }}>QR Scanner</span> <ion-icon name="qr-code-outline" style={{ color: 'white' }}></ion-icon></div>
                     <div style={{ marginTop: 10, fontSize: 14, color: 'white' }}>Click to Scan & Pay</div>
                  </div>
               </div>

               {/* SEARCH HISTORY & RECENT */}
               <div style={{ marginBottom: 30 }}>
                  <h3 style={{ fontSize: 18, marginBottom: 15, color: '#1e3a8a' }}>Recent Search History</h3>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10 }}>
                     {['Pizza Near Me', 'Men\'s Fashion', 'Car Service', 'Tech Stores'].map((term, i) => (
                        <div key={i} style={{
                           background: 'white', padding: '8px 16px', borderRadius: 20,
                           border: '1px solid #bfdbfe', color: '#3b82f6', fontSize: 14, whiteSpace: 'nowrap',
                           display: 'flex', alignItems: 'center', gap: 6
                        }}>
                           <ion-icon name="time-outline"></ion-icon> {term}
                        </div>
                     ))}
                  </div>
               </div>

               <h3 style={{ fontSize: 18, marginBottom: 15, color: '#1e3a8a' }}>New Restaurants & Stores</h3>
               {/* List of "New" places would go here - Mocking for now */}
               <div className="websites-list">
                  <div className="website-item" style={{ background: 'white' }}>
                     <div className="website-icon"><ion-icon name="fast-food"></ion-icon></div>
                     <div className="website-info">
                        <div className="website-name">Foodie Master</div>
                        <div className="website-meta">Burgers • 4.8 Stars</div>
                     </div>
                     <button className="btn-icon" onClick={() => navigate('/food-template-1/new')}><ion-icon name="arrow-forward"></ion-icon></button>
                  </div>
                  <div className="website-item" style={{ background: 'white' }}>
                     <div className="website-icon"><ion-icon name="shirt-outline"></ion-icon></div>
                     <div className="website-info">
                        <div className="website-name">Fashion Hub</div>
                        <div className="website-meta">Clothing • 4.5 Stars</div>
                     </div>
                     <button className="btn-icon"><ion-icon name="arrow-forward"></ion-icon></button>
                  </div>
               </div>
            </div>
         </div>
      );
   }

   // --- BUSINESS VIEW (Donezo Style) ---
   return (
      <div className="universal-dashboard">
         {/* SIDEBAR */}
         <aside className="dashboard-sidebar">
            <div className="sidebar-logo">
               <ion-icon name="layers"></ion-icon> WebDPro
            </div>

            <ul className="sidebar-menu">
               {['Dashboard', 'Websites', 'Analytics', 'Orders', 'Inventory', 'Tasks', 'Calendar', 'Team'].map(item => (
                  <li key={item} className="sidebar-item">
                     <a href="#"
                        className={`sidebar-link ${activeTab === item.toLowerCase() ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setActiveTab(item.toLowerCase()); }}
                     >
                        <ion-icon name={
                           item === 'Dashboard' ? 'grid-outline' :
                              item === 'Websites' ? 'globe-outline' :
                                 item === 'Analytics' ? 'bar-chart-outline' :
                                    item === 'Orders' ? 'bag-handle-outline' :
                                       item === 'Inventory' ? 'cube-outline' :
                                          item === 'Tasks' ? 'checkmark-circle-outline' :
                                             item === 'Calendar' ? 'calendar-outline' : 'people-outline'
                        }></ion-icon>
                        {item}
                     </a>
                  </li>
               ))}
            </ul>

            <div className="sidebar-item">
               <a href="#" className="sidebar-link" onClick={handleLogout}>
                  <ion-icon name="settings-outline"></ion-icon> Settings
               </a>
            </div>
            <div className="sidebar-item">
               <a href="#" className="sidebar-link" onClick={handleLogout}>
                  <ion-icon name="log-out-outline"></ion-icon> Logout
               </a>
            </div>

            <div className="app-download-card">
               <h4>Download App</h4>
               <p>Get full analytics on mobile</p>
               <button className="btn-download">Download</button>
            </div>
         </aside>

         {/* MAIN CONTENT */}
         <main className="dashboard-main">
            <header className="dashboard-header">
               <div className="search-bar">
                  <ion-icon name="search-outline"></ion-icon>
                  <input type="text" placeholder="Search Task" />
               </div>

               <div className="header-actions">
                  <button className="btn-icon" onClick={refreshData} title="Refresh data">
                     <ion-icon name="refresh"></ion-icon>
                  </button>
                  <button className="btn-icon">
                     <ion-icon name="notifications-outline"></ion-icon>
                  </button>
                  <div className="user-profile-sm">
                     <img src={`https://ui-avatars.com/api/?name=${user.name}&background=random`} alt="User" className="avatar-sm" />
                     <div>
                        <div style={{ fontWeight: 600 }}>{user.name}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>Business Owner</div>
                     </div>
                  </div>
               </div>
            </header>

            <div className="dashboard-heading" style={{ marginBottom: 30 }}>
               <h2 style={{ fontSize: 28, marginBottom: 8 }}>Website Inventory</h2>
               <p style={{ color: '#64748b' }}>Manage your digital assets and generated websites.</p>
            </div>

            {/* STATS */}
            <div className="stats-grid">
               <div className="stat-card highlight">
                  <div className="stat-header"><span>Total Sales</span> <ion-icon name="cash-outline"></ion-icon></div>
                  <div className="stat-value">₹{dashboardStats.totalSales?.toLocaleString() || '0'}</div>
                  <div className="stat-trend"><ion-icon name="trending-up"></ion-icon> Across all websites</div>
               </div>
               <div className="stat-card">
                  <div className="stat-header"><span>Total Orders</span> <ion-icon name="bag-check-outline"></ion-icon></div>
                  <div className="stat-value">{dashboardStats.totalOrders || 0}</div>
                  <div className="stat-trend">Across all websites</div>
               </div>
               <div className="stat-card">
                  <div className="stat-header"><span>Active Websites</span> <ion-icon name="globe-outline"></ion-icon></div>
                  <div className="stat-value">{dashboardStats.activeWebsites || 0}</div>
                  <div className="stat-trend">Live & operational</div>
               </div>
               <div className="stat-card">
                  <div className="stat-header"><span>Total Projects</span> <ion-icon name="layers-outline"></ion-icon></div>
                  <div className="stat-value">{dashboardStats.totalProjects || 0}</div>
                  <div className="stat-trend">All created projects</div>
               </div>
            </div>

            {/* CONTENT SECTIONS */}
            <div className="dashboard-sections">
               {activeTab === 'dashboard' && (
                  <>
                  <div className="section-column">
                     <div className="section-card">
                        <div className="section-header">
                           <h3 className="section-title">Your Generated Websites</h3>
                           <button className="btn-icon" onClick={() => navigate('/sector-selection')}>
                              <ion-icon name="add"></ion-icon>
                           </button>
                        </div>

                        {generatedWebsites.length > 0 ? (
                           <div className="websites-list">
                              {generatedWebsites.map((site, i) => (
                                 <div key={i} className="website-item">
                                    <div className="website-icon">
                                       <ion-icon name="globe-outline"></ion-icon>
                                    </div>
                                    <div className="website-info">
                                       <div className="website-name">{site.name}</div>
                                       <div className="website-meta">
                                          <span>{site.sector}</span> •
                                          <span style={{ color: site.status === 'Live' ? '#10b981' : 'orange' }}>{site.status}</span>
                                       </div>
                                    </div>
                                    <div className="website-sales" style={{ fontWeight: 700 }}>{site.sales}</div>
                                    <div className="website-actions">
                                       <button className="btn-icon" onClick={() => navigate(site.url)}>
                                          <ion-icon name="eye-outline"></ion-icon>
                                       </button>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        ) : (
                           <div className="null-state">
                              <div className="null-icon"><ion-icon name="browsers-outline"></ion-icon></div>
                              <p style={{ marginBottom: 16, color: '#64748b' }}>You haven't generated any websites yet.</p>
                              <button className="btn-primary" onClick={() => navigate('/sector-selection')}>
                                 Create New Website
                              </button>
                           </div>
                        )}
                     </div>

                     <div className="section-card">
                        <div className="section-header">
                           <h3 className="section-title">Project Analytics</h3>
                        </div>
                        {/* Analytics Chart */}
                        {analyticsData ? (
                           <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 8 }}>
                              {analyticsData.salesData?.slice(0, 7).map((dayData, i) => (
                                 <div key={i} style={{ width: '100%', height: `${Math.min(100, dayData.amount / 1000)}%`, background: '#10b981', borderRadius: 4, minWidth: '20px' }}>
                                    <div style={{ fontSize: '10px', textAlign: 'center', color: 'white', transform: 'rotate(-45deg)', transformOrigin: 'bottom center', marginTop: '-15px' }}>
                                       {dayData.amount?.toLocaleString() || 0}
                                    </div>
                                 </div>
                              )) || [40, 60, 30, 80, 50, 70, 45].map((h, i) => (
                                 <div key={i} style={{ width: '100%', height: `${h}%`, background: i === 3 ? '#10b981' : '#eef2f6', borderRadius: 4 }}></div>
                              ))}
                           </div>
                        ) : (
                           <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                              <p>No analytics data available</p>
                           </div>
                        )}
                     </div>
                  </div>

                  <div className="section-column">
                     <div className="section-card">
                        <h3 className="section-title" style={{ marginBottom: 16 }}>Project Progress</h3>
                        <div style={{ position: 'relative', width: 150, height: 150, margin: '0 auto' }}>
                           <div style={{
                              width: '100%', height: '100%', borderRadius: '50%',
                              background: 'conic-gradient(#10b981 75%, #eef2f6 0)'
                           }}></div>
                           <div style={{
                              position: 'absolute', inset: 10, background: 'white', borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexDirection: 'column'
                           }}>
                              <span style={{ fontSize: 24, fontWeight: 700 }}>75%</span>
                              <span style={{ fontSize: 12, color: '#64748b' }}>Completed</span>
                           </div>
                        </div>
                     </div>

                     <div className="section-card" style={{ background: '#0f172a', color: 'white' }}>
                        <h3 className="section-title" style={{ color: 'white', marginBottom: 16 }}>Next Meeting</h3>
                        <p style={{ opacity: 0.8, fontSize: 14, marginBottom: 24 }}>Discussing new automobile template features with the team.</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                           <span>10:00 AM</span>
                           <button style={{ background: '#10b981', border: 'none', borderRadius: 20, padding: '8px 16px', color: 'white', cursor: 'pointer' }}>Join</button>
                        </div>
                     </div>
                  </div>
                  </>
               )}

               {activeTab === 'websites' && (
                  <div className="section-column" style={{ width: '100%' }}>
                     <div className="section-card">
                        <div className="section-header">
                           <h3 className="section-title">All Websites</h3>
                           <button className="btn-icon" onClick={() => navigate('/sector-selection')}>
                              <ion-icon name="add"></ion-icon>
                           </button>
                        </div>

                        {generatedWebsites.length > 0 ? (
                           <div className="websites-list">
                              {generatedWebsites.map((site, i) => (
                                 <div key={i} className="website-item">
                                    <div className="website-icon">
                                       <ion-icon name="globe-outline"></ion-icon>
                                    </div>
                                    <div className="website-info">
                                       <div className="website-name">{site.name}</div>
                                       <div className="website-meta">
                                          <span>{site.sector}</span> •
                                          <span style={{ color: site.status === 'Live' ? '#10b981' : 'orange' }}>{site.status}</span>
                                       </div>
                                    </div>
                                    <div className="website-sales" style={{ fontWeight: 700 }}>{site.sales}</div>
                                    <div className="website-actions">
                                       <button className="btn-icon" onClick={() => navigate(site.url)}>
                                          <ion-icon name="eye-outline"></ion-icon>
                                       </button>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        ) : (
                           <div className="null-state">
                              <div className="null-icon"><ion-icon name="browsers-outline"></ion-icon></div>
                              <p style={{ marginBottom: 16, color: '#64748b' }}>You haven't generated any websites yet.</p>
                              <button className="btn-primary" onClick={() => navigate('/sector-selection')}>
                                 Create New Website
                              </button>
                           </div>
                        )}
                     </div>
                  </div>
               )}

               {activeTab === 'analytics' && (
                  <div className="section-column" style={{ width: '100%' }}>
                     <div className="section-card">
                        <div className="section-header">
                           <h3 className="section-title">Analytics Overview</h3>
                        </div>
                        
                        {analyticsData ? (
                           <div className="analytics-content">
                              <div className="analytics-stats-grid">
                                 <div className="stat-card">
                                    <h4>Total Revenue</h4>
                                    <p className="stat-value">₹{analyticsData.totalRevenue?.toLocaleString() || '0'}</p>
                                 </div>
                                 <div className="stat-card">
                                    <h4>Avg Daily Sales</h4>
                                    <p className="stat-value">₹{Math.round(analyticsData.avgDailySales)?.toLocaleString() || '0'}</p>
                                 </div>
                                 <div className="stat-card">
                                    <h4>Top Performing Sector</h4>
                                    <p className="stat-value">{analyticsData.topPerformingSectors?.[0]?.sector || 'N/A'}</p>
                                 </div>
                              </div>
                              
                              <div className="analytics-charts">
                                 <h4>Sales Trend (Last 7 Days)</h4>
                                 <div style={{ height: 250, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 8, marginTop: 20 }}>
                                    {analyticsData.salesData?.map((dayData, i) => (
                                       <div key={i} style={{ width: '100%', height: `${Math.min(100, dayData.amount / 1000)}%`, background: '#3b82f6', borderRadius: 4, minWidth: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', padding: '5px 0' }}>
                                          <div style={{ fontSize: '12px', color: 'white', fontWeight: 'bold' }}>{dayData.amount?.toLocaleString() || 0}</div>
                                          <div style={{ fontSize: '10px', color: 'white', marginTop: '2px' }}>{new Date(dayData.date).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                       </div>
                                    )) || [40, 60, 30, 80, 50, 70, 45].map((h, i) => (
                                       <div key={i} style={{ width: '100%', height: `${h}%`, background: '#eef2f6', borderRadius: 4 }}></div>
                                    ))}
                                 </div>
                              </div>
                              
                              {analyticsData.topPerformingSectors && analyticsData.topPerformingSectors.length > 0 && (
                                 <div className="top-sectors" style={{ marginTop: 30 }}>
                                    <h4>Top Performing Sectors</h4>
                                    <div className="sectors-list">
                                       {analyticsData.topPerformingSectors.map((sector, i) => (
                                          <div key={i} className="sector-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                                             <span>{sector.sector}</span>
                                             <span style={{ fontWeight: 'bold' }}>₹{sector.sales?.toLocaleString()}</span>
                                          </div>
                                       ))}
                                    </div>
                                 </div>
                              )}
                           </div>
                        ) : (
                           <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                              <p>No analytics data available yet.</p>
                           </div>
                        )}
                     </div>
                  </div>
               )}

               {activeTab === 'orders' && (
                  <div className="section-column" style={{ width: '100%' }}>
                     <div className="section-card">
                        <div className="section-header">
                           <h3 className="section-title">Recent Orders</h3>
                        </div>
                        
                        {analyticsData && analyticsData.recentActivity && analyticsData.recentActivity.length > 0 ? (
                           <div className="orders-list">
                              {analyticsData.recentActivity.map((order, i) => (
                                 <div key={i} className="order-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '15px', borderBottom: '1px solid #e2e8f0' }}>
                                    <div>
                                       <div style={{ fontWeight: 'bold' }}>Order #{order.id}</div>
                                       <div style={{ fontSize: '14px', color: '#64748b' }}>{new Date(order.createdAt).toLocaleDateString()}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                       <div style={{ fontWeight: 'bold' }}>₹{order.total?.toLocaleString()}</div>
                                       <div style={{ fontSize: '14px', color: order.status === 'pending' ? 'orange' : order.status === 'confirmed' ? '#3b82f6' : '#10b981' }}>
                                          {order.status}
                                       </div>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        ) : (
                           <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                              <p>No recent orders available.</p>
                           </div>
                        )}
                     </div>
                  </div>
               )}
            </div>
         </main>
      </div>
   );
};

export default UniversalDashboard;
